import React, { useMemo, useCallback } from 'react';
import { checkCompErrors } from '../../utils/compErrorFlagging';

const EMPTY_USER_FILTER_SETTINGS = {};

/**
 * Anomaly Detection Configuration
 * Define rules for detecting anomalies by comparing comps to subject property
 */
export const ANOMALY_RULES = {
  
  // School District mismatches - only check SDCode, display error on SDName
  SDCode: {
    checkMismatch: true, // Flag when school district code doesn't match subject
    severity: 'error',
    relatedFields: ['SDName'] // Display error on SDName field
  },
  // SDName: removed - we only check SDCode and display error on SDName
  
  // Size comparison anomalies (comps > 1.4x subject for orange, > 2x for red)
  IFMV: {
    checkSizeComparison: true,
    maxRatio: 1.4, // Flag when comp is > 1.4x subject (orange)
    maxRatioRed: 2.0, // Flag when comp is > 2x subject (red)
    severity: 'warning'
  },
  Sqft: {
    checkSizeComparison: true,
    maxRatio: 1.4,
    maxRatioRed: 2.0,
    severity: 'warning'
  },
  Acres: {
    checkSizeComparison: true,
    maxRatio: 1.4,
    maxRatioRed: 2.0,
    severity: 'warning'
  },
  
  // Sale price checks - basic data quality and size comparison vs subject IFMV
  SalePrice: {
    checkZero: true, // Flag zero sale prices
    checkNegative: true, // Flag negative sale prices
    checkSizeComparison: true, // Compare to subject IFMV
    maxRatio: 1.4, // Flag when comp is > 1.4x subject IFMV (orange)
    maxRatioRed: 2.0, // Flag when comp is > 2x subject IFMV (red)
    severity: 'error'
  },
  Address: {
    checkEmpty: true, // Flag empty addresses
    severity: 'warning'
  },
  
  // Style comparison anomalies
  Style: {
    checkStyleCompatibility: true, // Flag incompatible property styles
    severity: 'error'
  },
  
  // Distance-based anomalies
  Distance: {
    checkDistance: true, // Flag comps that are too far away
    warningThreshold: 1.0, // Orange warning at 1+ miles
    errorThreshold: 3.0, // Red error at 3+ miles
    severity: 'warning'
  },
  
  // Year Built comparison anomalies
  YearBuilt: {
    checkYearDifference: true, // Flag comps that are significantly newer than subject
    warningThreshold: 25, // Orange warning when comp is 25+ years newer
    errorThreshold: 50, // Red error when comp is 50+ years newer
    severity: 'warning'
  }
};

// PCC compatibility matrix - centralized for easy maintenance
const PCC_COMPATIBILITY_MATRIX = {
  // 210 series compatibility
  210: {
    warning: [215, 240], // Orange warning for these
    error: [220, 230, 280] // Red error for these
  },
  // 215 series compatibility (inherits from 210)
  215: {
    warning: [210, 240],
    error: [220, 230, 280]
  },
  // 220 series compatibility
  220: {
    warning: [230],
    error: [210, 215, 240, 280] // Everything else is error
  },
  // 230 series compatibility
  230: {
    warning: [220],
    error: [210, 215, 240, 280] // Everything else is error
  },
  // 240 series compatibility
  240: {
    warning: [210],
    error: [220, 230, 280] // Everything else is error
  },
  // 280 series - incompatible with everything
  280: {
    warning: [],
    error: [210, 215, 220, 230, 240] // Everything else is error
  }
};

// Style compatibility configuration - centralized for easy maintenance
const STYLE_COMPATIBILITY_CONFIG = {
  // Colonial and Ranch are incompatible with each other
  incompatibleGroups: [
    ['Colonial', 'Ranch']
  ],
  // Town House is incompatible with all other styles
  townHouseIncompatible: true,
  townHouseValue: 'Town House'
};

// View anomaly configuration - centralized for easy maintenance
// - If subject is not Waterfront and comp is Waterfront => error
// - If subject has no value and comp is Waterview => warning
const VIEW_ANOMALY_CONFIG = {
  errorWhenCompWaterfrontSubjectNot: true,
  warningWhenCompWaterviewSubjectEmpty: true,
  subjectWaterfrontValue: 'Waterfront',
  compWaterfrontValue: 'Waterfront',
  compWaterviewValue: 'Waterview',
};

// Helper to evaluate style compatibility anomalies based on the above config
const evaluateStyleCompatibilityAnomalies = (subjectStyleRaw, compStyleRaw) => {
  const anomalies = [];
  const subjectStyle = (subjectStyleRaw || '').toString().trim();
  const compStyle = (compStyleRaw || '').toString().trim();

  if (!subjectStyle || !compStyle) return anomalies;

  // Check incompatible groups (e.g., Colonial vs Ranch)
  STYLE_COMPATIBILITY_CONFIG.incompatibleGroups.forEach(group => {
    const subjectInGroup = group.includes(subjectStyle);
    const compInGroup = group.includes(compStyle);
    
    if (subjectInGroup && compInGroup && subjectStyle !== compStyle) {
      anomalies.push({
        type: 'style_incompatibility',
        message: `Incompatible styles: Subject "${subjectStyle}" vs Comp "${compStyle}"`,
        severity: 'error',
      });
    }
  });

  // Check Town House incompatibility
  if (STYLE_COMPATIBILITY_CONFIG.townHouseIncompatible) {
    const subjectIsTownHouse = subjectStyle === STYLE_COMPATIBILITY_CONFIG.townHouseValue;
    const compIsTownHouse = compStyle === STYLE_COMPATIBILITY_CONFIG.townHouseValue;
    
    if ((subjectIsTownHouse && !compIsTownHouse) || (!subjectIsTownHouse && compIsTownHouse)) {
      anomalies.push({
        type: 'style_incompatibility',
        message: `Incompatible styles: Subject "${subjectStyle}" vs Comp "${compStyle}"`,
        severity: 'error',
      });
    }
  }

  return anomalies;
};

// Helper to evaluate PCC compatibility anomalies
const evaluatePCCCompatibilityAnomalies = (subjectPCCRaw, compPCCRaw) => {
  const anomalies = [];
  const subjectPCC = Math.floor(Number(subjectPCCRaw)); // Convert to integer
  const compPCC = Math.floor(Number(compPCCRaw)); // Convert to integer

  if (isNaN(subjectPCC) || isNaN(compPCC)) return anomalies;

  // Get compatibility rules for subject PCC
  const compatibilityRules = PCC_COMPATIBILITY_MATRIX[subjectPCC];
  if (!compatibilityRules) {
    // If subject PCC is not in our matrix, do simple match (only compatible with itself)
    if (subjectPCC !== compPCC) {
      anomalies.push({
        type: 'pcc_incompatible',
        message: `PCC ${compPCC} is incompatible with subject PCC ${subjectPCC}`,
        severity: 'error',
      });
    }
    return anomalies;
  }

  // Check if comp PCC is in error list
  if (compatibilityRules.error.includes(compPCC)) {
    anomalies.push({
      type: 'pcc_incompatible',
      message: `PCC ${compPCC} is incompatible with subject PCC ${subjectPCC}`,
      severity: 'error',
    });
  }
  // Check if comp PCC is in warning list
  else if (compatibilityRules.warning.includes(compPCC)) {
    anomalies.push({
      type: 'pcc_incompatible',
      message: `PCC ${compPCC} may be incompatible with subject PCC ${subjectPCC}`,
      severity: 'warning',
    });
  }
  // If not in warning or error lists, it's compatible (no anomaly)

  return anomalies;
};

// Helper to evaluate distance anomalies
const evaluateDistanceAnomalies = (compDistanceRaw) => {
  const anomalies = [];
  const compDistance = Number(compDistanceRaw);

  if (isNaN(compDistance) || compDistance < 0) return anomalies;

  // Check error threshold (3+ miles)
  if (compDistance >= 3.0) {
    anomalies.push({
      type: 'distance_too_far',
      message: `Comp is far away (${compDistance.toFixed(1)} miles)`,
      severity: 'error',
    });
  }
  // Check warning threshold (1-3 miles)
  else if (compDistance >= 1.0) {
    anomalies.push({
      type: 'distance_may_be_far',
      message: `May be far away (${compDistance.toFixed(1)} miles)`,
      severity: 'warning',
    });
  }

  return anomalies;
};

// Helper to evaluate view anomalies based on the above config
const evaluateViewAnomalies = (subjectViewRaw, compViewRaw) => {
  const anomalies = [];
  const subjectView = (subjectViewRaw || '').toString().trim();
  const compView = (compViewRaw || '').toString().trim();

  if (!compView) return anomalies;

  // Error: subject is not Waterfront and comp is Waterfront
  if (
    VIEW_ANOMALY_CONFIG.errorWhenCompWaterfrontSubjectNot &&
    compView === VIEW_ANOMALY_CONFIG.compWaterfrontValue &&
    subjectView !== VIEW_ANOMALY_CONFIG.subjectWaterfrontValue
  ) {
    anomalies.push({
      type: 'view_mismatch',
      message: 'Comp has Waterfront view while subject is not Waterfront',
      severity: 'error',
    });
  }

  // Warning: subject has no value and comp is Waterview
  if (
    VIEW_ANOMALY_CONFIG.warningWhenCompWaterviewSubjectEmpty &&
    !subjectView &&
    compView === VIEW_ANOMALY_CONFIG.compWaterviewValue
  ) {
    anomalies.push({
      type: 'view_mismatch',
      message: 'Comp has Waterview while subject has no view specified',
      severity: 'warning',
    });
  }

  return anomalies;
};

/**
 * Anomaly Detection Hook
 * Detects anomalies by comparing comps to subject property and using existing compErrorFlagging
 */
export const useAnomalyDetection = (rows, subject, columnVisibilityModel, setColumnVisibilityModel, userFilterSettings = EMPTY_USER_FILTER_SETTINGS, getAdjustments = null) => {
  // Detect anomalies in the data by comparing to subject
  const anomalies = useMemo(() => {
    const detectedAnomalies = new Map(); // Map of rowId -> field -> anomaly details
    
    // Skip if no subject data
    if (!subject) {
      return detectedAnomalies;
    }
    
    rows.forEach(row => {
      if (!row.id) return;
      
      // Skip blank rows (rows without addresses) - these are for adding new comps
      const isBlankRow = !row.Address || row.Address === '' || 
                        (row.PropertyInfo && (!row.PropertyInfo.Address || row.PropertyInfo.Address === ''));
      if (isBlankRow) return;
      
      // First, run the existing compErrorFlagging logic
      const compErrors = checkCompErrors(row, subject, userFilterSettings, getAdjustments);
      
      // Add compErrorFlagging errors as anomalies
      if (compErrors.errorCodes && compErrors.errorCodes.length > 0) {
        if (!detectedAnomalies.has(row.id)) {
          detectedAnomalies.set(row.id, new Map());
        }
        
        // Map error codes to appropriate fields
        compErrors.errorCodes.forEach(errorCode => {
          let targetField = 'general'; // Default field for general errors
          
          // Map specific error codes to relevant fields
          switch (errorCode) {
            case 'NALSale':
            case 'InventoryMismatch':
            case 'SaleConditionIssue':
              targetField = 'SalePrice';
              break;
            case 'BadIFMV':
              targetField = 'IFMV';
              break;
            case 'OutsideDateRange':
              targetField = 'SaleDate';
              break;
            case 'notMLS':
              targetField = 'MLSNumber';
              break;
            case 'AcreageFilter':
              targetField = 'Acres';
              break;
            case 'SquareFeetFilter':
              targetField = 'Sqft';
              break;
            case 'GrossAdjFilter':
            case 'NetAdjFilter':
              targetField = 'adj_price';
              break;
            case 'returnWindow':
              targetField = 'general';
              break;
            default:
              targetField = 'general';
          }
          
          const errorMessage = compErrors.errorMessages.find(msg => msg.code === errorCode);
          detectedAnomalies.get(row.id).set(targetField, [{
            type: 'comp_error_flag',
            message: errorMessage ? errorMessage.message : errorCode,
            severity: 'error',
            errorCode: errorCode
          }]);
        });
      }
      
      // Then run our custom comparison-based anomaly detection
      Object.entries(ANOMALY_RULES).forEach(([field, rules]) => {
        const compValue = row[field];
        const subjectValue = subject[field];
        const fieldAnomalies = [];
        
        // Skip if field doesn't exist in the row
        if (compValue === undefined || compValue === null) return;
        
        // Property class mismatch check
        if (rules.checkMismatch && subjectValue !== undefined && subjectValue !== null) {
          if (String(compValue).trim() !== String(subjectValue).trim()) {
            fieldAnomalies.push({
              type: 'mismatch',
              message: `Property class mismatch: Comp "${compValue}" vs Subject "${subjectValue}"`,
              severity: rules.severity || 'error'
            });
          }
        }
        
        // School District mismatch check - only check SDCode
        if (rules.checkMismatch && field === 'SDCode' && subjectValue !== undefined && subjectValue !== null) {
          if (String(compValue).trim() !== String(subjectValue).trim()) {
            fieldAnomalies.push({
              type: 'school_district_mismatch',
              message: `School District Code mismatch: Comp "${compValue}" vs Subject "${subjectValue}"`,
              severity: rules.severity || 'error'
            });
          }
        }
        
        // Size comparison checks (comps > 1.4x subject for orange, > 2x for red)
        if (rules.checkSizeComparison) {
          const compNum = Number(compValue);
          let subjectNum;
          let comparisonField;
          
          // For SalePrice, compare to subject's IFMV instead of subject's SalePrice
          if (field === 'SalePrice') {
            subjectNum = Number(subject.IFMV);
            comparisonField = 'IFMV';
          } else {
            subjectNum = Number(subjectValue);
            comparisonField = field;
          }
          
          if (!isNaN(compNum) && !isNaN(subjectNum) && subjectNum > 0) {
            const ratio = compNum / subjectNum;
            
            // Check for red threshold first (2x)
            if (rules.maxRatioRed && ratio > rules.maxRatioRed) {
              fieldAnomalies.push({
                type: 'size_comparison',
                message: `${field} is ${ratio.toFixed(2)}x larger than subject ${comparisonField} (${compNum.toLocaleString()} vs ${subjectNum.toLocaleString()})`,
                severity: 'error' // Red for > 2x
              });
            }
            // Check for orange threshold (1.4x)
            else if (ratio > rules.maxRatio) {
              fieldAnomalies.push({
                type: 'size_comparison',
                message: `${field} is ${ratio.toFixed(2)}x larger than subject ${comparisonField} (${compNum.toLocaleString()} vs ${subjectNum.toLocaleString()})`,
                severity: 'warning' // Orange for > 1.4x but <= 2x
              });
            }
          }
        }
        
        // Year difference checks (for YearBuilt field)
        if (rules.checkYearDifference && field === 'YearBuilt') {
          const compYear = Number(compValue);
          const subjectYear = Number(subjectValue);
          
          if (!isNaN(compYear) && !isNaN(subjectYear) && compYear > subjectYear) {
            const yearDifference = compYear - subjectYear;
            
            // Check error threshold first (50+ years)
            if (rules.errorThreshold && yearDifference >= rules.errorThreshold) {
              fieldAnomalies.push({
                type: 'year_difference',
                message: `Comp is ${yearDifference} years newer than subject (${compYear} vs ${subjectYear})`,
                severity: 'error' // Red for 50+ years newer
              });
            }
            // Check warning threshold (25+ years)
            else if (rules.warningThreshold && yearDifference >= rules.warningThreshold) {
              fieldAnomalies.push({
                type: 'year_difference',
                message: `Comp is ${yearDifference} years newer than subject (${compYear} vs ${subjectYear})`,
                severity: 'warning' // Orange for 25-49 years newer
              });
            }
          }
        }
        
        // Basic data quality checks
        if (rules.checkZero && compValue === 0) {
          fieldAnomalies.push({
            type: 'zero_value',
            message: 'Zero value detected',
            severity: 'error' // Always error for zero values
          });
        }
        
        if (rules.checkNegative && Number(compValue) < 0) {
          fieldAnomalies.push({
            type: 'negative_value',
            message: 'Negative value detected',
            severity: 'error' // Always error for negative values
          });
        }
        
        if (rules.checkEmpty && typeof compValue === 'string' && compValue.trim() === '') {
          fieldAnomalies.push({
            type: 'empty_value',
            message: 'Empty value detected',
            severity: 'warning' // Always warning for empty values
          });
        }
        
        // Store anomalies if any were found
        if (fieldAnomalies.length > 0) {
          if (!detectedAnomalies.has(row.id)) {
            detectedAnomalies.set(row.id, new Map());
          }
          
          // Merge with existing anomalies for this field
          const existingAnomalies = detectedAnomalies.get(row.id).get(field) || [];
          detectedAnomalies.get(row.id).set(field, [...existingAnomalies, ...fieldAnomalies]);
          
          // Also flag related fields for school district mismatches
          if (rules.relatedFields) {
            rules.relatedFields.forEach(relatedField => {
              if (!detectedAnomalies.get(row.id).has(relatedField)) {
                let relatedMessage;
                if (field === 'SDCode' && relatedField === 'SDName') {
                  relatedMessage = 'School districts are different - this comp is in a different school district than the subject property';
                } else {
                  relatedMessage = `Related field flagged due to ${field} mismatch`;
                }
                
                detectedAnomalies.get(row.id).set(relatedField, [{
                  type: 'related_field_anomaly',
                  message: relatedMessage,
                  severity: rules.severity || 'error'
                }]);
              }
            });
          }
        }
      });

      // PCC compatibility anomaly checks
      const pccAnomalies = evaluatePCCCompatibilityAnomalies(subject.PCC, row.PCC);
      if (pccAnomalies.length > 0) {
        if (!detectedAnomalies.has(row.id)) {
          detectedAnomalies.set(row.id, new Map());
        }
        const existing = detectedAnomalies.get(row.id).get('PCC') || [];
        detectedAnomalies.get(row.id).set('PCC', [...existing, ...pccAnomalies]);
      }

      // Style compatibility anomaly checks
      const styleAnomalies = evaluateStyleCompatibilityAnomalies(subject.Style, row.Style);
      if (styleAnomalies.length > 0) {
        if (!detectedAnomalies.has(row.id)) {
          detectedAnomalies.set(row.id, new Map());
        }
        const existing = detectedAnomalies.get(row.id).get('Style') || [];
        detectedAnomalies.get(row.id).set('Style', [...existing, ...styleAnomalies]);
      }

      // Distance anomaly checks
      const distanceAnomalies = evaluateDistanceAnomalies(row.Distance);
      if (distanceAnomalies.length > 0) {
        if (!detectedAnomalies.has(row.id)) {
          detectedAnomalies.set(row.id, new Map());
        }
        const existing = detectedAnomalies.get(row.id).get('Distance') || [];
        detectedAnomalies.get(row.id).set('Distance', [...existing, ...distanceAnomalies]);
      }

      // View-specific anomaly checks (data-driven via VIEW_ANOMALY_CONFIG)
      const viewAnomalies = evaluateViewAnomalies(subject.View, row.View);
      if (viewAnomalies.length > 0) {
        if (!detectedAnomalies.has(row.id)) {
          detectedAnomalies.set(row.id, new Map());
        }
        const existing = detectedAnomalies.get(row.id).get('View') || [];
        detectedAnomalies.get(row.id).set('View', [...existing, ...viewAnomalies]);
      }
    });
    
    return detectedAnomalies;
  }, [rows, subject, userFilterSettings, getAdjustments]); // Dependencies ensure re-evaluation when data changes
  
  // Get anomalies for a specific row and field
  const getFieldAnomalies = useCallback((rowId, field) => {
    return anomalies.get(rowId)?.get(field) || [];
  }, [anomalies]);
  
  // Check if a field has any anomalies
  const hasFieldAnomalies = useCallback((rowId, field) => {
    return getFieldAnomalies(rowId, field).length > 0;
  }, [getFieldAnomalies]);
  
  // Get all fields with anomalies for a row
  const getAnomalousFields = useCallback((rowId) => {
    const rowAnomalies = anomalies.get(rowId);
    return rowAnomalies ? Array.from(rowAnomalies.keys()) : [];
  }, [anomalies]);
  
  // Update column visibility to show anomalous fields
  const updateVisibilityForAnomalies = useCallback(() => {
    const fieldsToShow = new Set();
    
    // Collect all fields that have anomalies
    anomalies.forEach((rowAnomalies) => {
      rowAnomalies.forEach((_, field) => {
        // For school district mismatches, only show SDName, not SDCode
        if (field === 'SDCode') {
          fieldsToShow.add('SDName'); // Show SDName instead of SDCode
        } else {
          fieldsToShow.add(field);
        }
      });
    });
    
    // Update visibility model to show anomalous fields
    if (fieldsToShow.size > 0) {
      const newVisibilityModel = { ...columnVisibilityModel };
      fieldsToShow.forEach(field => {
        if (newVisibilityModel[field] === false) {
          newVisibilityModel[field] = true;
        }
      });
      
      // Only update if there are changes
      const hasChanges = Object.keys(newVisibilityModel).some(
        key => newVisibilityModel[key] !== columnVisibilityModel[key]
      );
      
      if (hasChanges) {
        setColumnVisibilityModel(newVisibilityModel);
      }
    }
  }, [anomalies, columnVisibilityModel, setColumnVisibilityModel]);
  
  // Get cell styling for anomalous fields
  const getAnomalyCellStyle = useCallback((rowId, field) => {
    const fieldAnomalies = getFieldAnomalies(rowId, field);
    
    if (fieldAnomalies.length === 0) {
      return {};
    }
    
    // Determine the highest severity
    const hasError = fieldAnomalies.some(anomaly => anomaly.severity === 'error');
    const hasWarning = fieldAnomalies.some(anomaly => anomaly.severity === 'warning');
    
    return {
      color: hasError ? '#d32f2f' : '#f57c00', // Red for errors, orange for warnings
      // Don't set display, alignItems, width, height - let DataGrid handle alignment
      // Border and background are handled by CSS classes applied to the cell
    };
  }, [getFieldAnomalies]);
  
  // Get tooltip content for anomalous fields
  const getAnomalyTooltip = useCallback((rowId, field) => {
    const fieldAnomalies = getFieldAnomalies(rowId, field);
    
    if (fieldAnomalies.length === 0) {
      return null;
    }
    
    return (
      <div style={{ maxWidth: '300px' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
          Anomalies detected in {field}:
        </div>
        {fieldAnomalies.map((anomaly, index) => (
          <div key={index} style={{ marginBottom: '2px' }}>
            <span style={{ 
              color: anomaly.severity === 'error' ? '#d32f2f' : '#f57c00',
              fontWeight: 'bold'
            }}>
              {anomaly.severity.toUpperCase()}:
            </span>
            {' '}{anomaly.message}
          </div>
        ))}
      </div>
    );
  }, [getFieldAnomalies]);
  
  return useMemo(
    () => ({
      anomalies,
      getFieldAnomalies,
      hasFieldAnomalies,
      getAnomalousFields,
      updateVisibilityForAnomalies,
      getAnomalyCellStyle,
      getAnomalyTooltip,
    }),
    [
      anomalies,
      getFieldAnomalies,
      hasFieldAnomalies,
      getAnomalousFields,
      updateVisibilityForAnomalies,
      getAnomalyCellStyle,
      getAnomalyTooltip,
    ]
  );
};

/**
 * Anomaly Detection Component
 * Wrapper component that provides anomaly detection context
 */
export const AnomalyDetector = ({ 
  children, 
  rows, 
  subject,
  columnVisibilityModel, 
  setColumnVisibilityModel,
  userFilterSettings = EMPTY_USER_FILTER_SETTINGS,
  getAdjustments = null
}) => {
  const anomalyDetection = useAnomalyDetection(rows, subject, columnVisibilityModel, setColumnVisibilityModel, userFilterSettings, getAdjustments);
  const contextValue = useMemo(() => anomalyDetection, [anomalyDetection]);
  
  return (
    <AnomalyContext.Provider value={contextValue}>
      {children}
    </AnomalyContext.Provider>
  );
};

// Create context for anomaly detection
export const AnomalyContext = React.createContext(null);

// Hook to use anomaly detection context
export const useAnomalyContext = () => {
  const context = React.useContext(AnomalyContext);
  if (!context) {
    throw new Error('useAnomalyContext must be used within an AnomalyDetector');
  }
  return context;
};

export default AnomalyDetector;
